import React, { useState, useEffect } from 'react';
import {
  getCommunityColor,
  getPersonDetailsBatch,
  formatPersonName,
  getStatusConfig,
} from '../../../services/networkIntelligence.service';
import InteractionsDetailModal from './InteractionsDetailModal';
import CommunityMembersModal from './CommunityMembersModal';

/**
 * Table component displaying connected persons with sorting, filtering,
 * and person details lookup for investigation.
 */
const ConnectedPersonsTable = ({ connectedPersons, onPersonClick, onPersonSelect, focalPersonId }) => {
  const [sortField, setSortField] = useState('depth');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterDepth, setFilterDepth] = useState('all');
  const [personDetails, setPersonDetails] = useState(new Map());
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Modal state
  const [interactionsModal, setInteractionsModal] = useState({ isOpen: false, person1Id: null, person2Id: null, count: 0 });
  const [communityModal, setCommunityModal] = useState({ isOpen: false, communityId: null });

  // Load person details when connected persons change
  useEffect(() => {
    if (connectedPersons && connectedPersons.length > 0) {
      loadPersonDetails();
    }
  }, [connectedPersons]);

  const loadPersonDetails = async () => {
    setIsLoadingDetails(true);
    try {
      const personIds = connectedPersons.map(p => p.personId);
      const details = await getPersonDetailsBatch(personIds);
      setPersonDetails(details);
    } catch (error) {
      console.error('Failed to load person details:', error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  if (!connectedPersons || connectedPersons.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-3">Connected Persons</h3>
        <p className="text-gray-500 text-sm">No connections found</p>
      </div>
    );
  }

  // Sort and filter data
  const sortedData = [...connectedPersons]
    .filter(p => filterDepth === 'all' || p.depth === parseInt(filterDepth))
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle name sorting
      if (sortField === 'name') {
        const aDetails = personDetails.get(a.personId);
        const bDetails = personDetails.get(b.personId);
        aVal = formatPersonName(aDetails);
        bVal = formatPersonName(bDetails);
      }

      // Handle null/undefined
      if (aVal === null || aVal === undefined) aVal = sortField === 'name' ? 'ZZZ' : 0;
      if (bVal === null || bVal === undefined) bVal = sortField === 'name' ? 'ZZZ' : 0;

      const comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return (
      <span className="ml-1 text-gray-400">
        {sortDirection === 'asc' ? '\u2191' : '\u2193'}
      </span>
    );
  };

  // Get unique depths for filter
  const depths = [...new Set(connectedPersons.map(p => p.depth))].sort();

  const handleRowClick = (person) => {
    const details = personDetails.get(person.personId);
    if (onPersonSelect) {
      onPersonSelect(person.personId, { ...person, details });
    }
    if (onPersonClick) {
      onPersonClick(person);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold">Connected Persons</h3>
          {isLoadingDetails && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <span className="animate-spin h-3 w-3 border-2 border-blue-500 border-t-transparent rounded-full"></span>
              Loading details...
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Filter by depth:</span>
          <select
            value={filterDepth}
            onChange={(e) => setFilterDepth(e.target.value)}
            className="text-sm border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All ({connectedPersons.length})</option>
            {depths.map(d => (
              <option key={d} value={d}>
                Depth {d} ({connectedPersons.filter(p => p.depth === d).length})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('name')}
              >
                Name <SortIcon field="name" />
              </th>
              <th
                className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('personId')}
              >
                ID <SortIcon field="personId" />
              </th>
              <th className="px-3 py-2 text-left">Status</th>
              <th
                className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('depth')}
              >
                Depth <SortIcon field="depth" />
              </th>
              <th
                className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('interactionCount')}
              >
                Interactions <SortIcon field="interactionCount" />
              </th>
              <th
                className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('connectionWeight')}
              >
                Strength <SortIcon field="connectionWeight" />
              </th>
              <th className="px-3 py-2 text-left">Community</th>
              <th className="px-3 py-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedData.map((person, idx) => {
              const details = personDetails.get(person.personId);
              const statusConfig = getStatusConfig(details?.status || details?.Status);

              return (
                <tr
                  key={`${person.personId}-${idx}`}
                  className="hover:bg-blue-50 cursor-pointer transition-colors"
                  onClick={() => handleRowClick(person)}
                >
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">
                      {details ? formatPersonName(details) : (
                        <span className="text-gray-400 italic">Loading...</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {person.personId}
                  </td>
                  <td className="px-3 py-2">
                    {details ? (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.color }}
                      >
                        {statusConfig.label}
                      </span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      person.depth === 1 ? 'bg-purple-100 text-purple-800' : 'bg-pink-100 text-pink-800'
                    }`}>
                      {person.depth === 1 ? 'Direct' : `Depth ${person.depth}`}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setInteractionsModal({
                          isOpen: true,
                          person1Id: focalPersonId,
                          person2Id: person.personId,
                          count: person.interactionCount || 0
                        });
                      }}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md hover:bg-cyan-50 transition-colors group"
                      title="Click to see co-location details"
                    >
                      <span className="font-medium text-cyan-600 group-hover:text-cyan-700">
                        {person.interactionCount || 0}
                      </span>
                      <span className="text-gray-400 text-xs group-hover:text-cyan-600">co-locations</span>
                      <svg className="w-3 h-3 text-gray-300 group-hover:text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className="h-2 rounded-full transition-all"
                          style={{
                            width: `${(person.connectionWeight || 0) * 100}%`,
                            backgroundColor: person.connectionWeight >= 0.7 ? '#EF4444' :
                              person.connectionWeight >= 0.4 ? '#F59E0B' : '#9CA3AF',
                          }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">
                        {((person.connectionWeight || 0) * 100).toFixed(0)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {person.communityId !== null && person.communityId !== undefined ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCommunityModal({
                            isOpen: true,
                            communityId: person.communityId
                          });
                        }}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-white hover:opacity-80 transition-opacity group"
                        style={{ backgroundColor: getCommunityColor(person.communityId) }}
                        title="Click to see community members"
                      >
                        #{person.communityId}
                        <svg className="w-3 h-3 opacity-70 group-hover:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </button>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(person);
                      }}
                      className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                      title="View details"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {sortedData.length === 0 && (
        <div className="text-center py-4 text-gray-500">
          No connections match the current filter
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <span>Showing {sortedData.length} of {connectedPersons.length} connections</span>
        <span className="text-blue-600">Click a row to view person details</span>
      </div>

      {/* Interactions Detail Modal */}
      <InteractionsDetailModal
        isOpen={interactionsModal.isOpen}
        onClose={() => setInteractionsModal({ isOpen: false, person1Id: null, person2Id: null, count: 0 })}
        person1Id={interactionsModal.person1Id}
        person2Id={interactionsModal.person2Id}
        interactionCount={interactionsModal.count}
      />

      {/* Community Members Modal */}
      <CommunityMembersModal
        isOpen={communityModal.isOpen}
        onClose={() => setCommunityModal({ isOpen: false, communityId: null })}
        communityId={communityModal.communityId}
        onPersonSelect={onPersonSelect}
      />
    </div>
  );
};

export default ConnectedPersonsTable;
